import { Loader2 } from 'lucide-react';
import moment from 'moment';
import { useRef } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import QuickScheduleOptions from './QuickScheduleOptions';

/**
 * Edit-text and change-time dialogs for a scheduled message. Fully controlled
 * by the `dialogs` object returned from useScheduledMessages so the side panel
 * and the inline thread view share one implementation.
 */
const ScheduledMessageDialogs = ({ dialogs }) => {
  const editTextareaRef = useRef(null);

  const {
    messageBeingEdited,
    editedText,
    setEditedText,
    closeEditDialog,
    saveEditedMessage,
    messageBeingRescheduled,
    editedScheduledDate,
    setEditedScheduledDate,
    closeRescheduleDialog,
    saveEditedDate,
    activeActionId,
  } = dialogs;

  return (
    <>
      <Dialog
        open={!!messageBeingEdited}
        onOpenChange={(open) => {
          if (!open) closeEditDialog();
        }}
      >
        <DialogContent
          className="font-sans"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            const textarea = editTextareaRef.current;
            if (!textarea) return;
            textarea.focus();
            const end = textarea.value.length;
            textarea.setSelectionRange(end, end);
          }}
        >
          <DialogHeader>
            <DialogTitle>Edit scheduled message</DialogTitle>
            <DialogDescription>Update the text the customer will receive.</DialogDescription>
          </DialogHeader>
          <Textarea
            ref={editTextareaRef}
            value={editedText}
            onChange={(event) => setEditedText(event.target.value)}
            className="min-h-[140px] max-md:min-h-[96px] max-md:text-base"
            maxLength={1600}
            autoCapitalize="sentences"
          />
          <p className="text-xs text-muted-foreground">
            {editedText.trim().length}/1600 characters
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeEditDialog}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={saveEditedMessage}
              disabled={dialogs.sendingBlocked || activeActionId === messageBeingEdited?._id || !editedText.trim()}
            >
              {activeActionId === messageBeingEdited?._id ? (
                <Loader2 className="mr-2 size-4 animate-spin" />
              ) : null}
              Save message
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!messageBeingRescheduled}
        onOpenChange={(open) => {
          if (!open) closeRescheduleDialog();
        }}
      >
        <DialogContent className="font-sans">
          <DialogHeader>
            <DialogTitle>Change scheduled time</DialogTitle>
            <DialogDescription>Choose the new date and time for this message.</DialogDescription>
          </DialogHeader>
          <QuickScheduleOptions
            selectedDate={editedScheduledDate}
            onSelect={(date) => setEditedScheduledDate(moment(date).format('YYYY-MM-DDTHH:mm'))}
          />
          <p className="text-xs font-medium text-muted-foreground">Or choose a date and time</p>
          <Input
            type="datetime-local"
            className="max-md:h-11 max-md:text-base"
            value={editedScheduledDate}
            min={moment().format('YYYY-MM-DDTHH:mm')}
            onChange={(event) => setEditedScheduledDate(event.target.value)}
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeRescheduleDialog}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={saveEditedDate}
              disabled={
                dialogs.sendingBlocked || activeActionId === messageBeingRescheduled?._id ||
                !editedScheduledDate ||
                new Date(editedScheduledDate) < new Date()
              }
            >
              {activeActionId === messageBeingRescheduled?._id ? (
                <Loader2 className="mr-2 size-4 animate-spin" />
              ) : null}
              Save time
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ScheduledMessageDialogs;
